import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { 
  Dialog, 
  DialogActions, 
  DialogContent, 
  DialogContentText, 
  DialogTitle, 
  TextField, 
  Button, 
  Stack,
  Alert,
  CircularProgress,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Box,
  Typography
} from '@mui/material';
import api from '../services/api';
import { API_URL } from '../config';
import axios, { AxiosError } from 'axios';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';

interface ApiErrorResponse {
  message?: string;
  error?: string;
}

interface ChangePasswordDialogProps {
  open: boolean;
  onClose: () => void;
  username?: string;
}

interface PasswordValidation {
  length: boolean;
  uppercase: boolean;
  lowercase: boolean;
  number: boolean;
  noQuotes: boolean;
}

const ChangePasswordDialog: React.FC<ChangePasswordDialogProps> = ({ 
  open, 
  onClose,
  username = ''
}) => {
  const { t } = useTranslation();

  // Formulário armazenado em referência
  const formRef = useRef({
    user: username || '',
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  
  // Estado de UI
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [step, setStep] = useState<'form' | 'confirmation'>('form');
  const [formErrors, setFormErrors] = useState<{[key: string]: string}>({});
  
  // Estado de validação da senha
  const [passwordValidation, setPasswordValidation] = useState<PasswordValidation>({
    length: false,
    uppercase: false,
    lowercase: false,
    number: false,
    noQuotes: true
  });
  
  // Estado para verificar se as senhas coincidem
  const [passwordsMatch, setPasswordsMatch] = useState(true);
  
  // Indicar se devemos mostrar os critérios de senha
  const [showPasswordCriteria, setShowPasswordCriteria] = useState(false);
  
  // Temporizador de redirecionamento
  const redirectTimerRef = useRef<NodeJS.Timeout | null>(null);
  
  // Atualizar o username quando recebido via props
  useEffect(() => {
    if (username) {
      formRef.current.user = username;
    }
  }, [username]);
  
  // Limpar temporizador ao desmontar
  useEffect(() => {
    return () => {
      if (redirectTimerRef.current) {
        clearTimeout(redirectTimerRef.current);
      }
    };
  }, []);
  
  // Reset do formulário
  const resetForm = () => {
    formRef.current = {
      user: username || '',
      currentPassword: '',
      newPassword: '',
      confirmPassword: ''
    };
    setFormErrors({});
    setError('');
    setStep('form');
    setShowPasswordCriteria(false);
    setPasswordsMatch(true);
    setPasswordValidation({
      length: false,
      uppercase: false,
      lowercase: false,
      number: false,
      noQuotes: true
    });
    
    if (redirectTimerRef.current) {
      clearTimeout(redirectTimerRef.current);
      redirectTimerRef.current = null;
    }
  };
  
  // Manipuladores de campo - sem validação em tempo real
  const handleUserChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    formRef.current.user = e.target.value;
  };
  
  const handleCurrentPasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    formRef.current.currentPassword = e.target.value;
  };
  
  const handleNewPasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    formRef.current.newPassword = e.target.value;
    
    // Verificar se a senha de confirmação já existe
    if (formRef.current.confirmPassword) {
      // Atualizar o estado de correspondência se o usuário já digitou a confirmação
      setPasswordsMatch(formRef.current.confirmPassword === e.target.value);
    }
  };
  
  const handleConfirmPasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    formRef.current.confirmPassword = e.target.value;
  };
  
  // Função para lidar com o evento onFocus do campo de nova senha
  const handleNewPasswordFocus = () => {
    setShowPasswordCriteria(true);
  };
  
  // Função para lidar com o evento onBlur do campo de nova senha
  const handleNewPasswordBlur = () => {
    // Validar a senha quando o campo perde o foco
    validatePassword(formRef.current.newPassword);
    
    // Se já existe uma confirmação, verificar se as senhas coincidem
    if (formRef.current.confirmPassword) {
      setPasswordsMatch(formRef.current.confirmPassword === formRef.current.newPassword);
    }
  };
  
  // Função para lidar com o evento onBlur do campo de confirmação de senha
  const handleConfirmPasswordBlur = () => {
    // Verificar se as senhas coincidem quando o campo perde o foco
    const match = formRef.current.confirmPassword === formRef.current.newPassword;
    setPasswordsMatch(match);
    
    // Atualizar o erro de formulário para a confirmação de senha
    if (!match && formRef.current.confirmPassword) {
      setFormErrors(prev => ({
        ...prev,
        confirmPassword: t("password.mismatch")
      }));
    } else {
      // Limpar o erro se as senhas coincidirem
      setFormErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors.confirmPassword;
        return newErrors;
      });
    }
  };
  
  // Função para validar a senha conforme critérios
  const validatePassword = (password: string) => {
    setPasswordValidation({
      length: password.length >= 6 && password.length <= 12,
      uppercase: /[A-Z]/.test(password),
      lowercase: /[a-z]/.test(password),
      number: /[0-9]/.test(password),
      noQuotes: !(/['"]/.test(password))
    });
  };
  
  // Verificar se a senha atende a todos os critérios
  const isPasswordValid = () => {
    const validation = passwordValidation;
    return validation.length && 
           validation.uppercase && 
           validation.lowercase && 
           validation.number && 
           validation.noQuotes;
  };
  
  // Verificar se o formulário está pronto para submissão
  const isFormValid = () => {
    const form = formRef.current;
    
    return form.user.trim() !== '' && 
           form.currentPassword !== '' && 
           form.newPassword !== '' && 
           form.confirmPassword !== '' && 
           isPasswordValid() && 
           passwordsMatch;
  };
  
  // Validação do formulário completo
  const validateForm = () => {
    const form = formRef.current;
    const errors: {[key: string]: string} = {};
    
    if (!form.user.trim()) {
      errors.user = t("password.userRequired");
    }
    
    if (!form.currentPassword) {
      errors.currentPassword = t("password.currentRequired");
    }
    
    // Validar a senha novamente antes de prosseguir
    validatePassword(form.newPassword);
    
    // Validação da nova senha
    if (!form.newPassword) {
      errors.newPassword = t("password.newRequired");
    } else if (!isPasswordValid()) {
      errors.newPassword = t("password.criteriaNotMet");
    }
    
    if (!form.confirmPassword) {
      errors.confirmPassword = t("password.confirmRequired");
    } else if (form.newPassword !== form.confirmPassword) {
      errors.confirmPassword = t("password.mismatch");
      setPasswordsMatch(false);
    } else {
      setPasswordsMatch(true);
    }
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };
  
  // Avançar para confirmação
  const handleGoToConfirmation = () => {
    if (!validateForm()) return;
    
    setStep('confirmation');
    
    // Configurar redirecionamento automático
    redirectTimerRef.current = setTimeout(() => {
      handleSubmitPassword();
    }, 15000);
  };
  
  // Voltar para formulário
  const handleBackToForm = () => {
    setStep('form');
    
    if (redirectTimerRef.current) {
      clearTimeout(redirectTimerRef.current);
      redirectTimerRef.current = null;
    }
  };
  
  const handleSubmitPassword = async () => {
    if (redirectTimerRef.current) {
      clearTimeout(redirectTimerRef.current);
      redirectTimerRef.current = null;
    }
    
    setLoading(true);
    setError('');
    
    try {
        const userEmail = localStorage.getItem('userEmail') || formRef.current.user;
        // Verificar token antes de prosseguir
      const currentToken = localStorage.getItem('token');
      if (!currentToken) {
        setError(t('password.noToken'));
        setLoading(false);
        return;
      }
      
      // Payload EXATO do Postman que funcionou
      const payload = {
        user: userEmail, 
        password: formRef.current.currentPassword,
        newPassword: formRef.current.newPassword
      };
      
      console.log("Enviando payload EXATO do Postman:", payload);
      
      // A URL vem do AMBIENTE. Antes era fixa em 'https://app.n0.pt:4210' —
      // o host de PRODUÇÃO —, então trocar a senha em homologação alterava a
      // senha real de um usuário real. Nenhuma tela pode escolher o servidor
      // por conta própria.
      const response = await axios({
        method: 'post',
        url: `${API_URL}/user/chpsw`,
        data: payload,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${currentToken}`
        }
      });
      
      console.log("Resposta bem-sucedida:", response.data);
      
      // IMPORTANTE: Desativar o estado de carregamento ANTES de redirecionar
      setLoading(false);
      
      // Mostrar mensagem de sucesso
      alert(t("password.success"));
      
      // Fazer logout
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      localStorage.removeItem('refreshToken');
      
      // Redirecionar IMEDIATAMENTE para evitar estado travado
      window.location.href = '/user/login';
      
    } catch (error) {
      console.error("Erro na alteração de senha:", error);
      
      let errorMessage = "Erro ao alterar a senha. ";
      
      if (axios.isAxiosError(error) && error.response) {
        const status = error.response.status;
        console.log(`Erro ${status}:`, error.response.data);
        
        if (status === 401) {
          errorMessage += t("password.checkCurrent");
        } else {
          errorMessage += t("password.errorCode", { status });
        }
      } else {
        errorMessage += t("password.connectionFailed");
      }
      
      setError(errorMessage);
      setLoading(false);
      setStep('form');
    }
    
    // Timeout de segurança - garantir que o loading seja desativado após 5 segundos
    setTimeout(() => {
      setLoading(false);
    }, 1000);
  };
  // Função para fechar o diálogo
  const handleCloseButton = () => {
    if (loading) return;
    
    resetForm();
    onClose();
  };
  
  // Componente de critérios de senha
  const PasswordCriteria = () => (
    <Box sx={{ mt: 2, mb: 2, bgcolor: 'background.paper', borderRadius: 1, p: 1.5 }}>
      <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold' }}>
        A nova senha deve:
      </Typography>
      <List dense disablePadding>
        <PasswordCriteriaItem 
          text={t("password.rule1")} 
          isValid={passwordValidation.length} 
        />
        <PasswordCriteriaItem 
          text={t("password.rule2")} 
          isValid={passwordValidation.uppercase} 
        />
        <PasswordCriteriaItem 
          text={t("password.rule3")} 
          isValid={passwordValidation.lowercase} 
        />
        <PasswordCriteriaItem 
          text={t("password.rule4")} 
          isValid={passwordValidation.number} 
        />
        <PasswordCriteriaItem 
          text={t("password.rule5")} 
          isValid={passwordValidation.noQuotes} 
        />
      </List>
    </Box>
  );
  
  // Item individual dos critérios
  const PasswordCriteriaItem = ({ text, isValid }: { text: string, isValid: boolean }) => (
    <ListItem disablePadding sx={{ py: 0.5 }}>
      <ListItemIcon sx={{ minWidth: 30 }}>
        {isValid ? 
          <CheckCircleIcon fontSize="small" color="success" /> : 
          <CancelIcon fontSize="small" color="error" />
        }
      </ListItemIcon>
      <ListItemText 
        primary={text} 
        primaryTypographyProps={{ 
          variant: 'body2',
          sx: { 
            color: isValid ? 'success.main' : 'error.main',
            fontWeight: isValid ? 'normal' : 'medium'
          } 
        }} 
      />
    </ListItem>
  );
  
  // Formulário para troca de senha
  const PasswordForm = () => (
    <>
      <DialogTitle>
        {t('password.title')}
      </DialogTitle>
      
      <DialogContent>
        <DialogContentText sx={{ mb: 2 }}>
          Preencha os campos abaixo para alterar sua senha.
        </DialogContentText>
        
        <Stack spacing={2} sx={{ mt: 1 }}>
          <TextField
            label={t("login.user")}
            defaultValue={formRef.current.user}
            onChange={handleUserChange}
            fullWidth
            error={!!formErrors.user}
            helperText={formErrors.user || ""}
            disabled={Boolean(username) || loading}
          />
          
          <TextField
            label={t("password.current")}
            type="password"
            defaultValue={formRef.current.currentPassword}
            onChange={handleCurrentPasswordChange}
            fullWidth
            error={!!formErrors.currentPassword}
            helperText={formErrors.currentPassword || ""}
            disabled={loading}
          />
          
          <TextField
            label={t("password.new")}
            type="password"
            defaultValue={formRef.current.newPassword}
            onChange={handleNewPasswordChange}
            onFocus={handleNewPasswordFocus}
            onBlur={handleNewPasswordBlur}
            fullWidth
            error={!!formErrors.newPassword}
            helperText={formErrors.newPassword || ""}
            disabled={loading}
          />
          
          {showPasswordCriteria && <PasswordCriteria />}
          
          <TextField
            label={t("password.confirm")}
            type="password"
            defaultValue={formRef.current.confirmPassword}
            onChange={handleConfirmPasswordChange}
            onBlur={handleConfirmPasswordBlur}
            fullWidth
            error={!!formErrors.confirmPassword}
            helperText={formErrors.confirmPassword || ""}
            disabled={loading}
          />
          
          {error && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {error}
            </Alert>
          )}
        </Stack>
      </DialogContent>
      
      <DialogActions>
        <Button 
          onClick={handleCloseButton} 
          disabled={loading}
        >
          {t('common.cancel')}
        </Button>
        <Button 
          onClick={handleGoToConfirmation} 
          variant="contained" 
          color="primary"
          disabled={loading}
        >
          {loading ? <CircularProgress size={24} /> : t("common.continue")}
        </Button>
      </DialogActions>
    </>
  );
  
  // Tela de confirmação
  const ConfirmationScreen = () => (
    <>
      <DialogTitle>
        {t('password.confirmTitle')}
      </DialogTitle>
      
      <DialogContent>
        <DialogContentText sx={{ mb: 2 }}>
          {t('password.confirmText')}
        </DialogContentText>
        
        <DialogContentText sx={{ mb: 2, fontWeight: 'bold' }}>
          Esta ação será executada automaticamente se nenhuma opção for selecionada em 15 segundos.
        </DialogContentText>
        
        <Alert severity="info" sx={{ mt: 2 }}>
          Para sua segurança, sua sessão atual será encerrada após a alteração da senha.
        </Alert>
      </DialogContent>
      
      <DialogActions>
        <Button 
          onClick={handleBackToForm}
          disabled={loading}
        >
          {t('common.back')}
        </Button>
        <Button 
          onClick={handleSubmitPassword} 
          variant="contained" 
          color="primary"
          disabled={loading}
        >
          {loading ? <CircularProgress size={24} /> : t("password.confirmChange")}
        </Button>
      </DialogActions>
    </>
  );
  
  return (
    <Dialog 
      open={open}
      onClose={(_, reason) => {
        if (reason === 'backdropClick' || reason === 'escapeKeyDown') {
          if (!loading) {
            handleCloseButton();
          }
        }
      }}
      disableEscapeKeyDown={loading}
      maxWidth="sm"
      fullWidth
    >
      {step === 'form' ? <PasswordForm /> : <ConfirmationScreen />}
    </Dialog>
  );
};

export default ChangePasswordDialog;